<template>
  <v-main class="page-form-data-overview">
    <portal to="app-bar">
      <v-app-bar
        app
        color="secondary"
        dark
      >
        <v-btn
          icon
          @click="$router.push({ name: 'home' })"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <v-toolbar-title>{{ $t('form.index.titles.sync') }}</v-toolbar-title>
      </v-app-bar>
    </portal>

    <v-dialog
      v-model="removeDialogOpen"
      persistent
      max-width="290"
    >
      <v-card>
        <v-card-title class="headline">
          {{ $t('form.index.delete') }}
        </v-card-title>
        <v-card-text>
          {{ $t('form.index.delete_warning') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="green darken-1"
            text
            @click="removeDialogOpen = false"
          >
            {{ $t('misc.cancel') }}
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="remove"
          >
            {{ $t('form.index.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container>
      <v-list two-line>
        <v-list-item-group
          multiple
        >
          <template v-for="(projectData, index) of data">
            <v-list-item :key="index">
              <template v-slot:default>
                <v-list-item-content>
                  <v-list-item-title v-text="projectDataTitle(index)" />
                  <v-list-item-subtitle
                    v-if="!projectData.sentAt"
                    v-text="$t('form.index.received-at', { date: $d(Date.parse(projectData.project.createdAt), 'long') })"
                  />
                  <v-list-item-subtitle v-text="$t('form.index.started-at', { date: $d(Date.parse(projectData.createdAt), 'long') })" />
                  <v-list-item-subtitle
                    v-if="projectData.sentAt && projectData.sentAt !== true"
                    v-text="$t('form.index.sent-at', { date: $d(Date.parse(projectData.sentAt), 'long') })"
                  />
                </v-list-item-content>

                <v-list-item-action>
                  <!-- TODO: Add sync now button
                  <v-btn
                    v-if="projectData.sentAt && projectData.sentAt !== true"
                    icon
                    @click.prevent="share(projectData)"
                  >
                    <v-icon color="grey lighten-1">
                      mdi-share
                    </v-icon>
                  </v-btn>
                  -->
                </v-list-item-action>
              </template>
            </v-list-item>

            <v-divider
              v-if="index + 1 < data.length || hasMore"
              :key="index + '_d'"
            />
          </template>
        </v-list-item-group>

        <v-skeleton-loader
          v-if="hasMore"
          ref="skeleton"
          v-intersect.quiet="loadNextPage"
          type="list-item-three-line"
          class="mx-auto"
        />
      </v-list>
    </v-container>
  </v-main>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';

export default {
  data: () => ({
    data: [],
    page: 0,
    hasMore: null,
    loadedProjects: {},
    shareDialogOpen: false,
    shareEmail: null,
    shareSelection: null,
    removeDialogOpen: false,
    removeSelection: null
  }),
  computed: mapGetters({
    currentGroup: 'auth/currentGroup',
    user: 'auth/user'
  }),
  created () {
    this.loadNextPage();
  },
  methods: {
    projectDataTitle (idx) {
      if (!this.data[idx].project) {
        return;
      }

      if (this.data[idx].project.nameField) {
        let val = null;
        if (this.data[idx].project.nameForm === "head") {
          if (this.data[idx].project.formHeadData) {
            val = this.data[idx].project.formHeadData[this.data[idx].project.nameField];
          }
        } else if (this.data[idx].data) {
          val = this.data[idx].data[this.data[idx].project.nameField];
        }

        return (val && typeof val === "string") ? val : this.data[idx].project.name;
      }

      return this.data[idx].project.name;
    },
    async loadNextPage () {
      if (this.hasMore === false) return false;

      this.hasMore = null;
      const selector = { synced: { $eq: false } };

      const { docs } = await this.$pouch.find({
        selector,
        fields: ['_id', 'data', 'projectId', 'createdAt', 'sentAt', 'externalId', '_rev'],
        limit: 15,
        skip: this.page * 15
      }, 'project_data');

      for (const projectData of docs) {
        let project;
        if (this.loadedProjects['' + projectData.projectId]) {
          project = this.loadedProjects['' + projectData.projectId];
        } else {
          try {
            project = await this.$pouch.get('' + projectData.projectId, {}, 'projects');
          } catch (e) { console.error(e); };
        }

        if (project) {
          projectData.project = project;
          this.addData(projectData);
        }
      }

      this.page++;
      this.$nextTick(() => {
        this.hasMore = docs.length >= 15;
      });

      return true;
    },
    addData (projectData) {
      for (let el of this.data) {
        if (el.externalId == projectData.externalId) {
          return;
        }
      }

      this.data.push(projectData);
    },
    async share (data) {
      if (!this.shareDialogOpen) {
        this.shareSelection = data;
        this.shareDialogOpen = true;
        return;
      }

      const loader = this.$loading.show();
      try {
        await axios.get(`/project/${this.shareSelection.projectId}/data/${this.shareSelection.externalId}/share`, {
          params: {
            email: this.shareEmail
          }
        });
        this.shareDialogOpen = false;
        loader.hide();
      } catch (e) {
        loader.hide();
      }
    },
    async remove (data) {
      if (!this.removeDialogOpen) {
        this.removeSelection = data;
        this.removeDialogOpen = true;
        return;
      }

      const loader = this.$loading.show();
      try {
        const doc = await this.$pouch.get(this.removeSelection._id, {}, 'project_data');
        await this.$pouch.remove(doc, {}, 'project_data');
        if (this.removeSelection.externalId) {
          axios.delete(`/project/${this.removeSelection.projectId}/data/${this.removeSelection.externalId}`);
        }
        this.data = this.data.filter((el) => el._id !== this.removeSelection._id);

        this.removeDialogOpen = false;
        loader.hide();

        // TODO: Figure out why data needs to be deleted twice
        setTimeout(async () => {
          const doc = await this.$pouch.get(this.removeSelection._id, {}, 'project_data');
          this.$pouch.remove(doc, {}, 'project_data');
        }, 200);
      } catch (e) {
        loader.hide();
      }
    }
  }
};
</script>
